import React, { useEffect, useMemo } from 'react';
import { Badge, Table } from 'reactstrap';
import ActionButton from '../../../Components/Common/ActionButton';
import { SelectField } from '../../../Components/Form';
import NegotiationStatusBadge from './NegotiationStatusBadge';
import {
  displayValue,
  formatPrice,
  formatTimerDisplay,
  remainingSeconds,
} from '../utils/formatters';
import { formatDateTime } from '../../../utils/format';
import { EmptyState } from '../../../Components/Common/ListStates';
import {
  canExtendCargo,
  CARGO_STATUS,
  getPartnerDisplayStatus,
  isCargoRunning,
  PARTNER_STATUS,
} from '../utils/negotiationStatus';

const HEADERS = [
  'Cargo',
  'Partner',
  'Offer',
  'Price',
  'Volume',
  'Timer',
  'Status',
];
// Non-empty sentinel: SelectField shows its placeholder for an empty value.
const ALL_PARTNERS = '__all__';

// Accepted line price when a line is accepted, else the BE `lastPrice`
// (latest non-withdrawn round across the partner's lines, be#455 E-1).
const PriceCell = ({ partner, unit }) => {
  const accepted = (partner.lines || []).find(
    l => l.statusLabel === PARTNER_STATUS.ACCEPTED
  );
  if (accepted) {
    return (
      <>
        {formatPrice(accepted.acceptedPrice, unit)}
        <span className='text-muted'> · accepted</span>
      </>
    );
  }
  return formatPrice(partner.lastPrice, unit);
};

const TimerCell = ({ cargo, nowMs, isSupplier }) => {
  if (!cargo.startedAt) {
    return isSupplier
      ? 'Waiting for Buyer / Not Started'
      : 'Not Configured / Not Started';
  }
  const deadline = `${formatDateTime(cargo.deadline)} WIB`;
  if (!isCargoRunning(cargo)) return deadline;
  const left = remainingSeconds(cargo.deadline, nowMs);
  return (
    <>
      <span className={left > 0 ? 'fw-semibold text-dark' : 'text-danger'}>
        {left > 0 ? formatTimerDisplay(left) : 'Expired'}
      </span>
      <div className='fs-11'>{deadline}</div>
    </>
  );
};

// One row per cargo (Start / Extend + timer), then one row per partner
// (label `Cargo A|P1`, room button + unread badge).
const CargoTable = ({
  cargoes = [],
  mode = 'buyer',
  canStartTimer = true,
  nowMs,
  unit,
  partnerFilter = null,
  onPartnerFilterChange,
  onStart,
  onExtend,
  onOpenRoom,
}) => {
  const isSupplier = mode === 'supplier';
  const sellerNames = useMemo(() => {
    const names = new Set();
    cargoes.forEach(c =>
      (c.partners || []).forEach(p => p.sellerName && names.add(p.sellerName))
    );
    return [...names].sort();
  }, [cargoes]);
  const partnerOptions = [
    { value: ALL_PARTNERS, label: 'All partners' },
    ...sellerNames.map(name => ({ value: name, label: name })),
  ];
  // Buyer only: the supplier sees just its own partner rows. A seller gone
  // after a refresh falls back to All partners.
  const filter =
    !isSupplier && sellerNames.includes(partnerFilter) ? partnerFilter : null;
  useEffect(() => {
    if (partnerFilter && !sellerNames.includes(partnerFilter)) {
      onPartnerFilterChange?.(null);
    }
  }, [partnerFilter, sellerNames, onPartnerFilterChange]);
  const shownCargoes = filter
    ? cargoes.filter(c => (c.partners || []).some(p => p.sellerName === filter))
    : cargoes;

  return (
    <>
      {!isSupplier && (
        <div className='mb-3' style={{ maxWidth: 320 }}>
          <SelectField
            label='Partner'
            name='cargoPartnerFilter'
            options={partnerOptions}
            value={filter || ALL_PARTNERS}
            onChange={value =>
              onPartnerFilterChange?.(
                value && value !== ALL_PARTNERS ? value : null
              )
            }
            clearable={false}
          />
        </div>
      )}
      <div className='table-responsive'>
        <Table className='align-middle table-nowrap mb-0'>
          <thead className='table-light'>
            <tr>
              {HEADERS.map(h => (
                <th key={h}>
                  <span className='text-uppercase fw-bold fs-12 text-muted'>
                    {h}
                  </span>
                </th>
              ))}
              <th className='text-center'>
                <span className='text-uppercase fw-bold fs-12 text-muted'>
                  Action
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {shownCargoes.length === 0 ? (
              <tr>
                <td colSpan={HEADERS.length + 1}>
                  <EmptyState
                    compact
                    icon='ri-ship-line'
                    title={
                      filter
                        ? 'No cargo for this partner'
                        : 'No cargo to negotiate'
                    }
                    emptyHint={
                      filter
                        ? 'Pick another partner, or All partners.'
                        : isSupplier
                          ? 'No cargo has been opened for negotiation with you yet.'
                          : 'No cargo in this package. Select offers in the Negotiation tab of the Tender Evaluation.'
                    }
                  />
                </td>
              </tr>
            ) : (
              shownCargoes.map(cargo => {
                const notStarted =
                  cargo.statusLabel === CARGO_STATUS.NOT_STARTED;
                const partners = cargo.partners || [];
                const shownPartners = filter
                  ? partners.filter(p => p.sellerName === filter)
                  : partners;
                return (
                  <React.Fragment key={cargo.id}>
                    <tr className='table-light'>
                      <td className='text-dark fw-semibold'>
                        {cargo.cargoName}
                      </td>
                      <td className='text-muted small'>
                        {filter ? `${shownPartners.length} of ` : ''}
                        {partners.length} partner
                        {partners.length === 1 ? '' : 's'}
                      </td>
                      <td />
                      <td />
                      <td className='text-dark'>
                        {displayValue(cargo.requirement?.cargo?.volume)}
                      </td>
                      <td className='text-muted small'>
                        <TimerCell
                          cargo={cargo}
                          nowMs={nowMs}
                          isSupplier={isSupplier}
                        />
                      </td>
                      <td>
                        <NegotiationStatusBadge code={cargo.statusLabel} />
                      </td>
                      <td className='text-center'>
                        {canStartTimer && notStarted ? (
                          <ActionButton
                            variant='primary'
                            size='sm'
                            icon='ri-timer-line'
                            className='text-nowrap fw-medium'
                            disabled={!partners.length}
                            onClick={() => onStart(cargo)}
                          >
                            Start & Set Timer
                          </ActionButton>
                        ) : canStartTimer && canExtendCargo(cargo) ? (
                          <ActionButton
                            variant='secondary'
                            size='sm'
                            icon='ri-time-line'
                            className='text-nowrap fw-medium'
                            onClick={() => onExtend(cargo)}
                          >
                            Extend
                          </ActionButton>
                        ) : null}
                      </td>
                    </tr>
                    {shownPartners.map(partner => (
                      <tr key={partner.id}>
                        <td className='text-dark fw-medium ps-4'>
                          {partner.label}
                        </td>
                        <td className='text-dark'>
                          {displayValue(partner.sellerName)}
                        </td>
                        <td className='text-dark small'>
                          {displayValue(partner.idOffer)}
                          {partner.kind === 'counter' ? (
                            <span className='text-muted'> · counter</span>
                          ) : null}
                        </td>
                        <td className='text-dark small'>
                          <PriceCell partner={partner} unit={unit} />
                        </td>
                        <td />
                        <td />
                        <td>
                          <NegotiationStatusBadge
                            code={getPartnerDisplayStatus(partner)}
                          />
                          {(partner.lines || []).length > 1 && (
                            <div className='fs-11 text-muted mt-1'>
                              {
                                partner.lines.filter(
                                  l => l.statusLabel === PARTNER_STATUS.ACCEPTED
                                ).length
                              }
                              /{partner.lines.length} lines accepted
                            </div>
                          )}
                        </td>
                        <td className='text-center'>
                          {notStarted ? (
                            <ActionButton
                              variant='secondary'
                              size='sm'
                              disabled
                            >
                              {isSupplier
                                ? 'Room Locked — Waiting for Buyer'
                                : 'Room Locked — Not Started'}
                            </ActionButton>
                          ) : (
                            <ActionButton
                              variant='primary'
                              size='sm'
                              icon='ri-door-open-line'
                              className='text-nowrap fw-medium'
                              onClick={() => onOpenRoom(partner)}
                            >
                              Open Negotiation Room
                              {partner.unreadCount > 0 ? (
                                <Badge
                                  color='danger'
                                  pill
                                  className='ms-2'
                                  aria-label={`${partner.unreadCount} unread messages`}
                                >
                                  {partner.unreadCount}
                                </Badge>
                              ) : null}
                            </ActionButton>
                          )}
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </Table>
      </div>
    </>
  );
};

export default CargoTable;
